import React, { Fragment, useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { Formik, Form } from "formik";
import ProductInfo from "./ProductInfo";
import Units from "./Units";
import { FormMode } from "../../../../constant";
import * as Yup from "yup";

const useFormEnabler = (mode, isFormShown) => {
  const [disabled, setDisabled] = useState(true);
  const [modalMode, setmodalMode] = useState(mode);

  useEffect(() => {
    setDisabled(mode === FormMode.View);
  }, [mode, isFormShown]);

  const handleClick = () => {
    setmodalMode(FormMode.Edit);
    setDisabled(false);
  };

  return {
    disabled,
    onEdit: handleClick,
    modalMode,
  };
};

const useTitle = (disabled, productNumber) => {
  const [title, setTitle] = useState("Add new License");

  useEffect(() => {
    if (disabled && productNumber) {
      setTitle(`License details of ${productNumber}`);
    } else if (!disabled && productNumber) {
      setTitle(`Edit License of ${productNumber}`);
    }
  }, [disabled]);

  return title;
};

const validationSchema = Yup.object().shape({
  productNumber: Yup.string().required("Product Number is required."),
  packageId: Yup.number()
    .required("Package Id is required.")
    .integer("Package Id must be a number.")
    .typeError("Package Id must be a number."),
  packageName: Yup.string().required("Package name is required."),
  featureIds: Yup.array()
    .of(
      Yup.number()
        .required("At least 1 Feature Id is required.")
        .integer("Feature Id/s must be a number.")
        .typeError("At least 1 Feature Id is required.")
    )
    .required("At least 1 Feature Id is required."),
  duration: Yup.number()
    .integer("Duration must be a number.")
    .typeError("Duration must be a number.")
    .required("Duration is required."),
  numberOfUnlock: Yup.number()
    .integer("Number of Unlock must be a number.")
    .typeError("Number of Unlock must be a number.")
    .required("Number of Unlock is required."),
  requiresOnline: Yup.boolean(),
  units: Yup.array().when("requiresOnline", {
    is: false,
    then: Yup.array().of(
      Yup.object().shape({
        family: Yup.number()
          .moreThan(-1, "Family is required.")
          .typeError("Family is required."),
        type: Yup.number()
          .moreThan(-1, "Type is required.")
          .typeError("Type is required."),
        versionStart: Yup.number()
          .required("Start is required.")
          .typeError("Start must be a number.")
          .nullable()
          .transform((currentValue, originalValue) =>
            originalValue.trim() === "" ? null : currentValue
          ),
        versionEnd: Yup.number()
          .required("End is required.")
          .typeError("End must be a number.")
          .nullable()
          .transform((currentValue, originalValue) =>
            originalValue.trim() === "" ? null : currentValue
          ),
      })
    ),
    otherwise: Yup.array().of(
      Yup.object().shape({
        family: Yup.number("Family is required.")
          .moreThan(-1, "Family is required.")
          .typeError("Family is required."),
        versionStart: Yup.number()
          .typeError("Start must be a number.")
          .nullable()
          .transform((currentValue, originalValue) =>
            originalValue.trim() === "" ? null : currentValue
          ),
        versionEnd: Yup.number()
          .typeError("End must be a number.")
          .nullable()
          .transform((currentValue, originalValue) =>
            originalValue.trim() === "" ? null : currentValue
          ),
      })
    ),
  }),
});

export const LicenseFormModal = ({
  license,
  mode,
  show,
  onModalClose,
  onFormSubmit,
  canDefineLicense,
}) => {
  const formState = useFormEnabler(mode, show);
  const title = useTitle(formState.disabled, license.productNumber);

  return (
    <Modal
      show={show}
      onHide={onModalClose}
      dialogClassName="modal-xl"
      backdrop="static"
    >
      <Modal.Header>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Formik
        initialValues={license}
        validationSchema={validationSchema}
        onSubmit={onFormSubmit}
      >
        {({ handleSubmit, isSubmitting }) => (
          <Fragment>
            <Modal.Body>
              <Form className="form-horizontal">
                <div className="row">
                  <div className="col-sm-6">
                    <ProductInfo
                      {...formState}
                      canDefineLicense={canDefineLicense}
                    />
                  </div>
                  <div className="col-sm-5">
                    <Units {...formState} />
                  </div>
                </div>
              </Form>
            </Modal.Body>
            <Modal.Footer>
              <div className="btn-toolbar pull-right">
                <button
                  type="button"
                  className="btn btn-grey"
                  onClick={onModalClose}
                  disabled={isSubmitting}
                >
                  Close
                </button>
                {!formState.disabled && (
                  <button
                    type="submit"
                    className="btn btn-primary"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                  >
                    Save{" "}
                    {isSubmitting && <i className="fa fa-spinner fa-spin" />}
                  </button>
                )}
              </div>
            </Modal.Footer>
          </Fragment>
        )}
      </Formik>
    </Modal>
  );
};
